import { supabase } from "../../supabase/supabase";

export interface CallStatsTotals {
  calls: number;
  connected: number;
  seconds: number;
  cost_total: number;
  cost_llm: number;
  cost_stt: number;
  cost_tts: number;
  cost_known_calls: number;
  latency_avg_ms: number | null;
  latency_p95_ms: number | null;
  latency_turns: number;
  tts_failovers: number;
  reschedules: number;
  errors: number;
  llm_ttft_avg_ms?: number | null;
  tts_ttfb_avg_ms?: number | null;
  stt_eou_avg_ms?: number | null;
  turns?: number;
  interrupted_turns?: number;
  calls_with_turns?: number;
}

export interface CallStatsDurationBuckets {
  channel: "phone" | "web";
  buckets: number[];
}

export interface CallStatsHangupCauseRow {
  cause: string;
  calls: number;
  connected: number;
}

export interface CallStatsOutcomeRow {
  outcome: string;
  calls: number;
}

export interface CallStatsTrendPoint {
  bucket: string;
  calls: number;
  connected: number;
  seconds: number;
  cost_total: number;
}

export interface CallStatsAssistantRow {
  assistant_id: string | null;
  name: string | null;
  calls: number;
  connected: number;
  seconds: number;
  cost_total: number;
}

export interface CallStatsProviderRow {
  kind: "tts" | "stt" | "llm";
  provider: string;
  model: string | null;
  calls: number;
  seconds: number;
  cost: number;
}

export interface CallStatsEndedReasonRow {
  reason: string;
  calls: number;
}

export interface CallStatsHeatmapCell {
  dow: number;
  hour: number;
  calls: number;
}

// "web" is a web (video) session, "phone" is every other call.
export type CallStatsChannel = "web" | "phone";

export interface CallStatsChannelRow {
  channel: CallStatsChannel;
  calls: number;
  connected: number;
  seconds: number;
}

export interface CallStatsChannelReasonRow extends CallStatsEndedReasonRow {
  channel: CallStatsChannel;
}

export type CallStatsGranularity = "hour" | "day" | "week";

export interface CallDashboardStats {
  range: {
    from: string;
    to: string;
    prev_from: string;
    prev_to: string;
    rolled_until: string | null;
    granularity: CallStatsGranularity;
    tz: string;
  };
  totals: {
    current: CallStatsTotals;
    previous: CallStatsTotals;
  };
  trend: CallStatsTrendPoint[];
  by_assistant: CallStatsAssistantRow[];
  by_provider: CallStatsProviderRow[];
  ended_reasons: CallStatsEndedReasonRow[];
  // Missing when the database still runs the RPC from before the channel split.
  by_channel?: CallStatsChannelRow[];
  ended_reasons_by_channel?: CallStatsChannelReasonRow[];
  duration_buckets?: CallStatsDurationBuckets[];
  hangup_causes?: CallStatsHangupCauseRow[];
  outcomes?: CallStatsOutcomeRow[];
  heatmap: CallStatsHeatmapCell[];
}

export async function fetchCallDashboardStats(
  from: Date,
  to: Date,
  tz: string,
  prevFrom: Date,
  prevTo: Date,
): Promise<CallDashboardStats> {
  const { data, error } = await (supabase as any).rpc(
    "get_call_dashboard_stats",
    {
      p_from: from.toISOString(),
      p_to: to.toISOString(),
      p_tz: tz,
      p_prev_from: prevFrom.toISOString(),
      p_prev_to: prevTo.toISOString(),
    },
  );
  if (error) throw new Error(error.message);
  return data as CallDashboardStats;
}
